import React, { useEffect, useMemo, useRef, useState } from "react";
import { RLayer, RSource, useMap } from "maplibre-react-components";
import type {
  ExpressionSpecification,
  FilterSpecification,
  FillLayerSpecification,
  LineLayerSpecification,
  Map as MapLibreMap,
  MapLayerMouseEvent,
  MapMouseEvent,
} from "maplibre-gl";
import { EDA_SCALE, makeConditionalStepExpression } from "@/utils/helpers";
import {
  buildHexgroupIdFilter,
  getActiveEdaHexgroupIds,
} from "@/utils/mapFilters";
import { DEFAULT_SOURCE_TABLE } from "@/store/AdminToolsProvider";
import { useAgglomerationsDataContext } from "@/store/AgglomerationsProvider";
import {
  buildSelection,
  buildSavedZoneSelection,
  dedupeFeatures,
  DEFAULT_COLOR,
  getLassoSelection,
  isHexFeature,
  TEMPLATE_STOPS,
} from "./helpers";
import { useAdminPriceEdaContext } from "./context";
import type { ScreenPoint } from "./types";

const SOURCE_ID = "hex-editor-src";
const ZONE_SOURCE_ID = "hex-editor-zone-src";
const LAYER_ID = "hex-editor-fill";
const LINE_LAYER_ID = "hex-editor-line";
const ZONE_FILL_ID = "hex-editor-zone-fill";
const ZONE_LINE_ID = "hex-editor-zone-line";
const SELECTION_FILL_ID = "hex-editor-selection-fill";
const SELECTION_LINE_ID = "hex-editor-selection-line";

const buildIdFilter = (
  ids: number[],
  baseFilter: FilterSpecification
): FilterSpecification => {
  if (!ids.length) {
    return ["==", "id", -1] as FilterSpecification;
  }

  return [
    "all",
    baseFilter,
    ["in", ["to-string", ["get", "id"]], ["literal", ids.map(String)]],
  ] as FilterSpecification;
};

const buildPendingFillColorExpression = (
  pendingColors: Record<number, string>
): ExpressionSpecification | string => {
  const entries = Object.entries(pendingColors);

  if (!entries.length) {
    return DEFAULT_COLOR;
  }

  return [
    "match",
    ["to-string", ["get", "id"]],
    ...entries.flatMap(([id, color]) => [id, color]),
    DEFAULT_COLOR,
  ] as ExpressionSpecification;
};

const buildEditorZoneFilter = (
  baseFilter: FilterSpecification
): FilterSpecification => {
  return ["all", baseFilter] as FilterSpecification;
};

export const EditorLayers = () => {
  const map = useMap() as MapLibreMap | null;
  const { agglomerations } = useAgglomerationsDataContext();
  const {
    selection: { onSelect, selectedIds },
    visuals: { pendingColors, priceRangesMap, priceField, refreshToken },
    interaction: { removeMode, editorMode, panMode },
  } = useAdminPriceEdaContext();
  const [lassoPoints, setLassoPoints] = useState<ScreenPoint[]>([]);
  const lassoPointsRef = useRef<ScreenPoint[]>([]);
  const dragPanWasEnabledRef = useRef(false);

  const tiles = useMemo(
    () => [
      `${__MARTIN_URL__}/${DEFAULT_SOURCE_TABLE}/{z}/{x}/{y}.pbf?rt=${refreshToken}`,
    ],
    [refreshToken]
  );
  const zoneTiles = useMemo(
    () => [
      `${__MARTIN_URL__}/public.editor_zones/{z}/{x}/{y}.pbf?rt=${refreshToken}`,
    ],
    [refreshToken]
  );
  const activeHexgroupIds = useMemo(
    () => getActiveEdaHexgroupIds(agglomerations),
    [agglomerations]
  );
  const activeHexgroupFilter = useMemo(
    () => buildHexgroupIdFilter(activeHexgroupIds),
    [activeHexgroupIds]
  );

  const fillLayer = useMemo<FillLayerSpecification>(
    () => ({
      id: LAYER_ID,
      type: "fill",
      source: SOURCE_ID,
      "source-layer": DEFAULT_SOURCE_TABLE,
      filter: activeHexgroupFilter,
      paint: {
        "fill-color": makeConditionalStepExpression(
          priceField,
          priceRangesMap,
          EDA_SCALE,
          TEMPLATE_STOPS,
          "relative",
          DEFAULT_COLOR
        ),
        "fill-opacity": 0.5,
      },
    }),
    [activeHexgroupFilter, priceField, priceRangesMap]
  );

  const lineLayer = useMemo<LineLayerSpecification>(
    () => ({
      id: LINE_LAYER_ID,
      type: "line",
      source: SOURCE_ID,
      "source-layer": DEFAULT_SOURCE_TABLE,
      filter: activeHexgroupFilter,
      paint: {
        "line-color": "#FFB347",
        "line-width": 0.5,
        "line-opacity": 0.28,
      },
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
    }),
    [activeHexgroupFilter]
  );

  const zoneFillLayer = useMemo<FillLayerSpecification>(
    () => ({
      id: ZONE_FILL_ID,
      type: "fill",
      source: ZONE_SOURCE_ID,
      "source-layer": "public.editor_zones",
      filter: buildEditorZoneFilter(activeHexgroupFilter),
      paint: {
        "fill-color": "#11d5c7",
        "fill-opacity": 0.02,
      },
    }),
    [activeHexgroupFilter]
  );

  const zoneLineLayer = useMemo<LineLayerSpecification>(
    () => ({
      id: ZONE_LINE_ID,
      type: "line",
      source: ZONE_SOURCE_ID,
      "source-layer": "public.editor_zones",
      filter: buildEditorZoneFilter(activeHexgroupFilter),
      paint: {
        "line-color": "#6b6358",
        "line-width": 2.5,
        "line-opacity": 0.96,
      },
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
    }),
    [activeHexgroupFilter]
  );

  const selectionFillLayer = useMemo<FillLayerSpecification>(
    () => ({
      id: SELECTION_FILL_ID,
      type: "fill",
      source: SOURCE_ID,
      "source-layer": DEFAULT_SOURCE_TABLE,
      filter: buildIdFilter(selectedIds, activeHexgroupFilter),
      paint: {
        "fill-color": "#11d5c7",
        "fill-opacity": 0.6,
      },
    }),
    [activeHexgroupFilter, selectedIds]
  );

  const selectionLineLayer = useMemo<LineLayerSpecification>(
    () => ({
      id: SELECTION_LINE_ID,
      type: "line",
      source: SOURCE_ID,
      "source-layer": DEFAULT_SOURCE_TABLE,
      filter: buildIdFilter(selectedIds, activeHexgroupFilter),
      paint: {
        "line-color": "#042f3a",
        "line-width": 3,
      },
    }),
    [activeHexgroupFilter, selectedIds]
  );

  const pendingUpdateIds = useMemo(
    () => Object.keys(pendingColors).map(Number).filter(Number.isFinite),
    [pendingColors]
  );

  const pendingFillLayer = useMemo<FillLayerSpecification>(
    () => ({
      id: "hex-editor-pending-fill",
      type: "fill",
      source: SOURCE_ID,
      "source-layer": DEFAULT_SOURCE_TABLE,
      filter: buildIdFilter(pendingUpdateIds, activeHexgroupFilter),
      paint: {
        "fill-color": buildPendingFillColorExpression(pendingColors),
        "fill-opacity": 1,
      },
    }),
    [activeHexgroupFilter, pendingUpdateIds, pendingColors]
  );

  const clearLasso = () => {
    lassoPointsRef.current = [];
    setLassoPoints([]);
  };

  const restoreDragPan = () => {
    if (!map) return;
    if (dragPanWasEnabledRef.current) {
      map.dragPan.enable();
    }
    dragPanWasEnabledRef.current = false;
  };

  const handleHexClick = (
    e: MapLayerMouseEvent,
    selectMode: "single" | "group" | "zone"
  ) => {
    if (editorMode === "lasso") return;

    const clicked = e.features?.[0];
    if (!isHexFeature(clicked) || !map) return;
    e.preventDefault();

    const allFeatures = dedupeFeatures(
      map.querySourceFeatures(SOURCE_ID, {
        sourceLayer: DEFAULT_SOURCE_TABLE,
      })
    );
    let nextSelection: typeof allFeatures = [clicked];
    if (selectMode === "zone") {
      nextSelection = buildSavedZoneSelection(clicked, allFeatures);
    } else if (selectMode === "group") {
      nextSelection = buildSelection(clicked, allFeatures);
    }

    onSelect(nextSelection, [e.point.x, e.point.y], {
      append: Boolean(e.originalEvent.ctrlKey || e.originalEvent.metaKey),
      remove: removeMode,
    });
  };

  useEffect(() => {
    if (!map) return;

    const handleContextMenu = (e: MapLayerMouseEvent) =>
      handleHexClick(e, "single");

    map.on("contextmenu", LAYER_ID, handleContextMenu);
    return () => {
      map.off("contextmenu", LAYER_ID, handleContextMenu);
    };
  }, [editorMode, map, removeMode, onSelect]);

  useEffect(() => {
    if (!map) return;

    const canvas = map.getCanvas();
    let cursor = "";
    if (editorMode === "lasso") {
      cursor = panMode ? "grab" : "crosshair";
    }
    canvas.style.cursor = cursor;

    return () => {
      canvas.style.cursor = "";
    };
  }, [editorMode, map, panMode]);

  useEffect(() => {
    if (editorMode === "lasso" && !panMode) return;
    restoreDragPan();
    clearLasso();
  }, [editorMode, panMode]);

  useEffect(() => {
    if (!map) return;

    const container = map.getCanvasContainer();

    const toLocalPoint = (event: MouseEvent): ScreenPoint => {
      const bounds = container.getBoundingClientRect();
      return {
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      };
    };

    const handleMouseDown = (e: MapMouseEvent) => {
      if (editorMode !== "lasso" || panMode || e.originalEvent.button !== 0) {
        return;
      }

      dragPanWasEnabledRef.current = map.dragPan.isEnabled();
      if (dragPanWasEnabledRef.current) {
        map.dragPan.disable();
      }

      const nextPoints = [{ x: e.point.x, y: e.point.y }];
      lassoPointsRef.current = nextPoints;
      setLassoPoints(nextPoints);
      e.preventDefault();
    };

    const handleMouseMove = (event: MouseEvent) => {
      if (editorMode !== "lasso") return;

      setLassoPoints((prev) => {
        if (!prev.length) return prev;

        const nextPoint = toLocalPoint(event);
        const lastPoint = prev[prev.length - 1];
        const dx = nextPoint.x - lastPoint.x;
        const dy = nextPoint.y - lastPoint.y;

        if (dx * dx + dy * dy < 16) {
          return prev;
        }

        const nextPoints = [...prev, nextPoint];
        lassoPointsRef.current = nextPoints;
        return nextPoints;
      });
    };

    const handleMouseUp = (event: MouseEvent) => {
      const prev = lassoPointsRef.current;
      if (!prev.length) return;

      restoreDragPan();

      const releasePoint = toLocalPoint(event);
      const points =
        prev[prev.length - 1]?.x === releasePoint.x &&
        prev[prev.length - 1]?.y === releasePoint.y
          ? prev
          : [...prev, releasePoint];

      lassoPointsRef.current = [];
      setLassoPoints([]);

      if (points.length < 3) {
        return;
      }

      const renderedFeatures = dedupeFeatures(
        map.queryRenderedFeatures(undefined, {
          layers: [LAYER_ID],
        })
      );
      const nextSelection = getLassoSelection(renderedFeatures, points, map);
      const anchor = points[points.length - 1] || points[0];

      onSelect(nextSelection, [anchor.x, anchor.y], {
        append: Boolean(event.ctrlKey || event.metaKey),
        remove: removeMode,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    map.on("mousedown", handleMouseDown);

    return () => {
      restoreDragPan();
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      map.off("mousedown", handleMouseDown);
    };
  }, [editorMode, map, onSelect, panMode, removeMode]);

  const lassoPath = useMemo(() => {
    if (!lassoPoints.length) return "";

    return lassoPoints
      .map(
        (point, index) =>
          `${index === 0 ? "M" : "L"} ${point.x.toFixed(1)} ${point.y.toFixed(
            1
          )}`
      )
      .join(" ");
  }, [lassoPoints]);

  return (
    <>
      <RSource
        id={SOURCE_ID}
        type="vector"
        tiles={tiles}
        minzoom={0}
        maxzoom={14}
      />
      <RSource
        id={ZONE_SOURCE_ID}
        type="vector"
        tiles={zoneTiles}
        minzoom={0}
        maxzoom={14}
      />
      <RLayer
        {...fillLayer}
        onClick={(e) =>
          handleHexClick(e, e.originalEvent.altKey ? "zone" : "group")}
      />
      <RLayer {...lineLayer} />
      <RLayer {...zoneFillLayer} />
      <RLayer {...zoneLineLayer} />
      <RLayer {...pendingFillLayer} />
      <RLayer {...selectionFillLayer} />
      <RLayer {...selectionLineLayer} />
      {lassoPoints.length > 1 && (
        <svg
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 5,
          }}
          aria-hidden="true"
        >
          <path
            d={lassoPath}
            fill="rgba(17, 213, 199, 0.16)"
            stroke="#11d5c7"
            strokeWidth="2"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
      )}
    </>
  );
};
